// Private Yoga Trowbridge - Service + Location Page (SEO Enhanced)
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Car, Check, Users, Clock, Heart } from "lucide-react";
import Navigation from "../../../components/shared/Navigation";
import Footer from "../../../components/shared/Footer";
import PageBreadcrumb from "../../../components/shared/PageBreadcrumb";
import { usePageMeta, generateServiceSchema, generateFAQSchema, SchemaScript } from "../../../components/shared/SEOHelper";

const PrivateYogaTrowbridge = () => {
  usePageMeta({
    title: "Private Yoga Trowbridge | 1-to-1 Yoga | Annelie Lily",
    description: "Private yoga sessions for Trowbridge residents. One-to-one yoga tailored to your body and goals, based in Frome, just 15 minutes from Trowbridge.",
    canonical: "https://annelielily.com/areas/yoga-trowbridge/private-yoga"
  });

  const serviceSchema = generateServiceSchema({
    name: "Private Yoga Sessions for Trowbridge Residents",
    description: "One-to-one private yoga sessions for Trowbridge residents at Annelie Lily Yoga in Frome. Personalised practice tailored to your body, goals, and pace.",
    serviceType: "Private Yoga Sessions",
    url: "https://annelielily.com/areas/yoga-trowbridge/private-yoga"
  });

  const faqs = [
    { question: "What happens in a private yoga session?", answer: "We start by discussing your goals and any health considerations. Then I design a practice around you — the poses, pace, and focus all adapt to what you need. Each session responds to how you're feeling that day." },
    { question: "How far is Frome from Trowbridge?", answer: "Just 15 minutes via the A361. It's a straightforward drive, and many Trowbridge residents come weekly for private sessions." },
    { question: "How long are sessions?", answer: "Typically 60 minutes, though 90-minute sessions are available. We work at your pace without rushing." },
    { question: "Is private yoga suitable for complete beginners?", answer: "Absolutely. Private sessions are actually ideal for beginners — you can learn the foundations properly with individual attention before joining group classes, or continue one-to-one if you prefer." },
    { question: "Can I focus on specific issues like back pain?", answer: "Yes. Many students come specifically to work with injuries, chronic pain, or post-surgery recovery. We adapt every session to support your body safely." },
  ];

  const faqSchema = generateFAQSchema(faqs);

  return (
    <div className="min-h-screen bg-cream">
      <SchemaScript schema={serviceSchema} />
      <SchemaScript schema={faqSchema} />
      <Navigation />
      <PageBreadcrumb currentPage="Private Yoga" currentSlug="areas/yoga-trowbridge/private-yoga" />
      
      {/* Hero */}
      <section className="pt-8 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">Private Yoga for Trowbridge</span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-neutral-800 mb-5">Private Yoga Sessions Trowbridge</h1>
            <p className="text-neutral-700 leading-relaxed max-w-2xl mx-auto mb-6">
              One-to-one yoga for Trowbridge residents. A practice designed entirely around you — 
              your body, your goals, your pace. Just 15 minutes from Trowbridge.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700"><MapPin className="w-4 h-4 text-gold" />Based in Frome</span>
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700"><Car className="w-4 h-4 text-gold" />15 min from Trowbridge</span>
            </div>
            <Link to="/contact" className="btn-primary" data-testid="hero-cta">Book a Private Session <ArrowRight className="w-4 h-4 inline ml-2" /></Link>
          </motion.div>
        </div>
      </section>

      {/* Featured Snippet / Direct Answer */}
      <section className="py-10 px-6 md:px-12 bg-cream border-b border-gold/10">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-xl md:text-2xl text-neutral-800 mb-3">What is private yoga?</h2>
          <p className="text-neutral-700">
            Private yoga is one-to-one instruction where every aspect of the session is tailored to you. Unlike group classes, the poses, pace, and focus all adapt to your body and goals. It's ideal for beginners, those with injuries, or anyone who prefers individual attention.
          </p>
        </div>
      </section>
      
      {/* Who Is This For */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8">Who are private sessions for?</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { title: "Complete beginners", desc: "Learn properly with individual attention before joining a group, or continue privately" },
              { title: "Working with injuries", desc: "Back pain, joint issues, post-surgery recovery — we adapt practice to support your body" },
              { title: "Specific goals", desc: "Flexibility, strength, stress management, or preparing for something specific" },
              { title: "Busy schedules", desc: "Flexible timing that fits around work and life commitments" },
            ].map((item, i) => (
              <div key={i} className="bg-white/70 p-5 rounded-xl border border-gold/10">
                <h3 className="font-heading text-lg text-neutral-800 mb-2">{item.title}</h3>
                <p className="text-neutral-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8">What to expect in a session</h2>
          <div className="space-y-6 text-neutral-700">
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-gold font-semibold text-sm">1</span>
              </div>
              <div>
                <h3 className="font-heading text-lg text-neutral-800 mb-1">Initial conversation</h3>
                <p className="text-sm">We discuss your goals, any injuries or health considerations, and what you hope to get from the session.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-gold font-semibold text-sm">2</span>
              </div>
              <div>
                <h3 className="font-heading text-lg text-neutral-800 mb-1">Tailored practice</h3>
                <p className="text-sm">I'll guide you through a practice designed for your body. We might draw on <Link to="/areas/yoga-trowbridge/hatha-yoga" className="text-gold hover:underline">Hatha</Link>, <Link to="/areas/yoga-trowbridge/yin-yoga" className="text-gold hover:underline">Yin</Link>, or a combination — whatever serves you best.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-gold font-semibold text-sm">3</span>
              </div>
              <div>
                <h3 className="font-heading text-lg text-neutral-800 mb-1">Individual attention</h3>
                <p className="text-sm">With one-to-one focus, I can offer detailed guidance and adjustments that aren't possible in a group.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-gold font-semibold text-sm">4</span>
              </div>
              <div>
                <h3 className="font-heading text-lg text-neutral-800 mb-1">Guided relaxation</h3>
                <p className="text-sm">Every session ends with proper relaxation — time for your body to absorb the practice.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Trowbridge Residents Choose Us */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">Why Trowbridge residents travel to Frome</h2>
          <div className="prose-content space-y-4 text-neutral-700">
            <p>
              <Link to="/areas/yoga-trowbridge" className="text-gold hover:underline">Trowbridge</Link> is just 15 minutes from Frome via the A361 — close enough that private yoga sessions can become a regular part of your routine. Many Trowbridge residents make this short journey weekly.
            </p>
            <p>
              The studio offers a calm, unhurried environment away from town. For private sessions especially, that sense of stepping away from everyday life helps you arrive ready to focus on yourself.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-10 text-center">Benefits of private yoga</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Users, text: "Undivided attention" },
              { icon: Clock, text: "Flexible scheduling" },
              { icon: Heart, text: "Adapted to your body" },
              { icon: Check, text: "Faster progress" },
            ].map((item, i) => (
              <div key={i} className="bg-white/70 p-5 rounded-xl border border-gold/10 flex items-center gap-3">
                <item.icon className="w-5 h-5 text-gold flex-shrink-0" />
                <span className="text-neutral-700">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8 text-center">Questions about private yoga in Trowbridge</h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white/70 p-5 rounded-xl border border-gold/10">
                <h3 className="font-heading text-lg text-neutral-800 mb-2">{faq.question}</h3>
                <p className="text-neutral-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-12 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-neutral-700 mb-4">
            I'm Annelie, a qualified yoga teacher with experience working one-to-one with students of all levels. 
            <Link to="/about" className="text-gold hover:underline ml-1">Learn more about my approach</Link>, or 
            <Link to="/about" className="text-gold hover:underline ml-1">read what students say</Link>.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">Ready to book a private session?</h2>
          <p className="text-neutral-600 mb-6">Get in touch to discuss your goals and find a time that works.</p>
          <Link to="/contact" className="btn-primary" data-testid="bottom-cta">Book a Session <ArrowRight className="w-4 h-4 inline ml-2" /></Link>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-12 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-neutral-600 text-sm mb-4">Also available for Trowbridge residents:</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/areas/yoga-trowbridge/hatha-yoga" className="text-sm text-gold hover:underline">Hatha Yoga Trowbridge</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/areas/yoga-trowbridge/yin-yoga" className="text-sm text-gold hover:underline">Yin Yoga Trowbridge</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/areas/yoga-trowbridge" className="text-sm text-gold hover:underline">All Classes Trowbridge</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/yoga-classes/private-yoga" className="text-sm text-gold hover:underline">Private Sessions (Main)</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivateYogaTrowbridge;
